import React, { useState, useEffect } from 'react';
import { FAQModel } from '../../types/faq';
import Modal from '../../Common/Components/Modal';
import { toast } from 'react-toastify';

interface EditFAQDialogProps {
  faq: FAQModel;
  onClose: () => void;
  onSave: (faq: FAQModel) => Promise<void>;
  show: boolean;
}

const EditFAQDialog: React.FC<EditFAQDialogProps> = ({
  faq,
  onClose,
  onSave,
  show
}) => {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (faq) {
      setQuestion(faq.question);
      setAnswer(faq.answer);
    }
  }, [faq]);

  const handleSave = async () => {
    if (!question.trim() || !answer.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    try {
      const updatedFAQ: FAQModel = {
        ...faq,
        question: question.trim(),
        answer: answer.trim(),
      };

      await onSave(updatedFAQ);
      onClose();
    } catch (error) {
      console.error('Error updating FAQ:', error);
      toast.error('Failed to update FAQ. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} id="editFAQModal" modal-center="true"
      className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
      dialogClassName="w-[calc(100vw-2rem)] mx-4 md:mx-0 md:w-[40rem] bg-white shadow rounded-md dark:bg-zink-600 flex flex-col h-full">
      <Modal.Header className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500"
        closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500 dark:text-zink-200 dark:hover:text-red-500">
        <Modal.Title className="text-16">Edit FAQ</Modal.Title>
      </Modal.Header>
      <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Question *</label>
            <input
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-custom-500 dark:bg-zink-700 dark:border-zink-500"
              placeholder="Enter the FAQ question"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Answer *</label>
            <textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-custom-500 dark:bg-zink-700 dark:border-zink-500"
              placeholder="Enter the FAQ answer"
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="flex items-center justify-between p-4 mt-auto border-t border-slate-200 dark:border-zink-500">
        <button
          onClick={handleClose}
          className="px-4 py-2 text-sm bg-slate-100 hover:bg-slate-200 dark:bg-zink-600 dark:hover:bg-zink-500 rounded-md transition-colors"
          disabled={isLoading}
        >
          Close
        </button>
        <button
          onClick={handleSave}
          disabled={isLoading}
          className="px-4 py-2 text-sm bg-custom-500 text-white hover:bg-custom-600 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isLoading && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          )}
          Update FAQ
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditFAQDialog;